import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { DeepPartial } from '@shared/directives/types';
import { HttpClient } from '@angular/common/http';
import { ApolloUserService } from '@core/graphql/apolloUser.service';
import { ApolloQuotationService } from '@core/graphql/apolloQuotation.service';
import { UserPoco, QuotationPoco, QuotationDto, RoadTaxRequestDto } from '@data/DTO/DTO';
import { environment } from 'environments/environment.qc';
import { findPostcode } from 'malaysia-postcodes';
type VehicleType = 'Car';

@Component({
    selector: 'app-roadtax',
    templateUrl: './roadtax.component.html',
    styleUrls: ['./roadtax.component.scss']
})
export class RoadTaxComponent implements OnInit {
    personInCharge: any;
    isYesSelected: boolean = false;
    createForm!: FormGroup;
    submitted = false;
    loading: boolean = false;
    showAlert: boolean = false;
    alertProperties = { type: '', message: '' };
    modalState: boolean = false;
    modalTemplate: any = {};
    modalSize: any = {};
    isError: boolean = false;
    loginErrorMessage: string = '';
    engineCC: number | null = null;

    constructor(private formBuilder: FormBuilder, private route: ActivatedRoute, private router: Router, private userService: ApolloUserService,
        private quotationService: ApolloQuotationService, private http: HttpClient
    ) { }
    @ViewChild('saveTemplateHeader', { static: true }) saveTemplateHeader!: TemplateRef<object>;
    @ViewChild('saveTemplateBody', { static: true }) saveTemplateBody!: TemplateRef<object>;
    @ViewChild('saveTemplateFooter', { static: true }) saveTemplateFooter!: TemplateRef<object>;
    ngOnInit() {
        this.handleGuid();
        this.initForm();
    }

    initForm() {
        this.createForm = this.formBuilder.group({
            plateNumber: [null, Validators.required],
            nric: [null, Validators.required],
            name: [null, Validators.required],
            isEhailing: [false, Validators.required],
            email: [null, Validators.required],
            phoneNumber: [null, Validators.required],
            postcode: [null, Validators.required],
            vehicleType: [null, Validators.required],
            carType: [null, Validators.required],
            fuelType: [null, Validators.required],
            ownershipType: [null, Validators.required],
        });
    }

    handleGuid() {
        this.route.queryParams.subscribe(params => {
            const guid = params['guid'];
            if (guid) {
                if (guid === 'null') {
                    localStorage.removeItem('guid');
                } else {
                    localStorage.setItem('guid', guid);
                    this.getPersonInCharge(guid);
                }
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { guid: null },
                    queryParamsHandling: 'merge'
                });
            } else {
                const storedGuid = localStorage.getItem('guid');
                if (storedGuid) {
                    this.getPersonInCharge(storedGuid);
                }
            }
        });
    }

    getPersonInCharge(guid: string) {
        this.userService.getListUser().subscribe(users => {
            const user = users?.data?.find((u: UserPoco) => u.guid.toLowerCase() === guid.toLowerCase());
            if (user) {
                this.personInCharge = user;
            } else {
                console.log('User not found!');
            }
        });
    }

    selectYes() {
        this.isYesSelected = true;
        this.createForm.get('isEhailing')?.setValue(true);
    }

    selectNo() {
        this.isYesSelected = false;
        this.createForm.get('isEhailing')?.setValue(false);
    }

    onVehicleTypeChange(selectedType: any) {
        const fv = this.createForm.value;
        const motorCalculatePremiumBody = {
            quotationNo: "",
            productId: 3,
            productCode: "MI",
            isRenewal: false,
            sumInsured: 0,
            agentCode: "",
            agentType: 0,
            isStaff: false,
            customer: {
                identityType: "1",
                identityNumber: fv.nric || '',
                ethnicityId: null
            },
            vehicle: {
                nvic: "",
                registrationNo: fv.plateNumber,
                useAgreedValue: false,
                postcode: fv.postcode || '',
                chassisNo: "",
                engineNo: ""
            },
            addOn: [],
            hasPersonalAccident: false,
            personalAccident: {
                productCode: "",
                planClass: 1
            },
            language: "en",
            isPartner: false,
            countryCode: "MYS",
            isMAEM2U: false
        };
        const API_URL = environment.API_URL;
        this.http.post<any>(`${API_URL}/EtiqaProxy/CalculatePremium`, motorCalculatePremiumBody).subscribe(
            (res: any) => {
                this.engineCC = res?.data?.value?.vix?.capacity || 0;
            },
            (error: any) => {
                console.error('Premium Calculation Error:', error);
            }
        );
    }

    onSave() {
        this.submitted = true;
        this.modalSize = { width: '500px', height: '250px' };
        this.modalState = true;
        this.modalTemplate = { header: this.saveTemplateHeader, body: this.saveTemplateBody, footer: this.saveTemplateFooter };
    }

    onSubmit() {
        this.submitted = true;
        this.loading = true;

        const fv = this.createForm.value;
        const postcodeInfo = findPostcode(fv.postcode);

        const payload: DeepPartial<QuotationDto> = {
            email: fv.email,
            isEhailing: fv.isEhailing,
            name: fv.name,
            nric: fv.nric,
            phoneNumber: fv.phoneNumber,
            platNumber: fv.plateNumber,
            postcode: fv.postcode,
            city: postcodeInfo.city,
            state: postcodeInfo.state,
            country: 'Malaysia',
            vehicleType: fv.vehicleType,
        };

        const roadtax: DeepPartial<RoadTaxRequestDto> = {
            engineCC: this.engineCC != null ? Number(this.engineCC) : undefined,
            ownershipType: fv.ownershipType,
            vehicleType: fv.carType,
            fuelType: fv.fuelType,
        };

        if (this.personInCharge?.id) {
            payload.picUser = this.personInCharge.id;
        }
        this.quotationService.requestQuotation(payload as Partial<QuotationDto>, roadtax as Partial<RoadTaxRequestDto>).subscribe({
            next: resp => {
                this.loading = false;
                if (resp.success) {
                    this.displayAlert('success', 'Quotation requested successfully!');
                    this.router.navigate(['/returnstatus'], {
                        queryParams: { status: 'success' }
                    });
                    this.createForm.reset();
                } else {
                    this.displayAlert('danger', resp.error || 'Failed to request quotation');
                    this.router.navigate(['/returnstatus'], {
                        queryParams: { status: 'failed' }
                    });
                }
                this.modalState = false;
                this.submitted = false;
            },
            error: () => {
                this.loading = false;
                this.displayAlert('danger', 'Error requesting quotation');
                this.router.navigate(['/returnstatus'], {
                    queryParams: { status: 'failed' }
                });
                this.modalState = false;
            }
        });
    }

    onCloseModal() {
        this.modalState = false;
    }

    displayAlert(type: string, message: string) {
        this.showAlert = true;
        this.alertProperties = { type: type, message: message }
        window.setTimeout(() => {
            this.showAlert = false;
        }, 5000)
    }
}